<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data/modules";
import type { PropType } from "vue";
import type { CreateElement } from "vue";
import type { IProvider } from "@/models/providers";
import type {
  IClientTemplateRenderProviderData,
  IClientTemplateRenderProviderMethods
} from "@/models/providers/templates";
import {
  ClientAreaTemplateTypes,
  type ClientTemplateSlotCodes
} from "@/data/enums/templates";
import type { IClientAreaTemplate, ITemplate } from "@/models/templates";

export default defineComponent({
  name: "ClientTemplateRenderProvider",
  mixins: [RequestPromises],
  provide() {
    return {
      clientTemplateRenderProvider: this.clientTemplateRenderProvider,
      clientTemplateRenderProviderMethods:
        this.clientTemplateRenderProviderMethods
    };
  },
  props: {
    code: { type: String as PropType<ClientTemplateSlotCodes>, required: true },
    objectId: { type: String, required: true },
    handleLoading: { type: Boolean, default: false },
    loaderAttrs: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    handleRequestError: { type: Boolean, default: false },
    withAccessToken: { type: Boolean, default: true },
    onNotFound: {
      type: Function as PropType<IProvider["onNotFound"]>,
      default: () => {}
    }
  },
  data: () => ({
    isLoading: true,
    isReloading: false,
    isProcessing: {},
    hasRequestError: false
  }),
  computed: {
    ...mapState({
      template(state, getters): ITemplate {
        return getters[`data/single`]({
          storeModule: DataModules.TEMPLATES_CLIENT_RENDER,
          scope: this.templateScope
        });
      },
      templateScope(state, getters): string {
        return getters[`data/${DataModules.TEMPLATES_CLIENT_RENDER}/scope`](
          this.code
        );
      }
    }),
    clientTemplateRenderProviderMethods(): IClientTemplateRenderProviderMethods {
      return {
        reloadData: this.reloadData
      };
    },
    isIframe() {
      return [ClientAreaTemplateTypes.CLIENT_AREA_IFRAME].includes(
        (this.template as IClientAreaTemplate)?.type
      );
    }
  },
  created() {
    this.isLoading = true;
    this.loadData();
  },
  beforeDestroy() {
    // Bin template data
    this.$store.dispatch("data/binSingle", {
      scope: this.templateScope,
      storeModule: DataModules.TEMPLATES_CLIENT_RENDER,
      vm: this
    });
  },
  methods: {
    async loadData({ ignoreStored = false } = {}) {
      return this.getTemplate(ignoreStored);
    },
    async getTemplate(ignoreStored = false): Promise<any> {
      try {
        await this.$store.dispatch(
          `data/${DataModules.TEMPLATES_CLIENT_RENDER}/get`,
          {
            code: this.code,
            payload: {
              scope: this.templateScope,
              callConfig: {
                withAccessToken: this.withAccessToken
              },
              ignoreStored,
              params: {
                object_id: this.objectId
              },
              vm: this
            }
          }
        );
        this.hasRequestError = false;
      } catch (error) {
        const is404 = this.$store.getters["api/is404"](error);
        if (is404 && this.onNotFound) this.onNotFound(this);
        this.hasRequestError = true;
      } finally {
        this.isLoading = false;
        this.isReloading = false;
      }
    },
    reloadData() {
      this.isReloading = true;
      return this.loadData({ ignoreStored: true });
    },
    clientTemplateRenderProvider(): IClientTemplateRenderProviderData {
      return {
        body: (this.template as IClientAreaTemplate)?.body,
        type: (this.template as IClientAreaTemplate)?.type,
        code: this.code,
        hasRequestError: this.hasRequestError,
        isLoading: this.isLoading,
        isReloading: this.isReloading,
        isIframe: this.isIframe,
        scope: this.templateScope
      };
    }
  },
  render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $templateData: this.clientTemplateRenderProvider(),
        $templateMethods: this.clientTemplateRenderProviderMethods
      })
    );
  }
});
</script>
